/**
 * Unit conversion utilities for lab values
 * Handles conversion between different unit systems (e.g., US vs International)
 */

export interface UnitConversion {
  fromUnit: string;
  toUnit: string;
  factor: number;
  offset?: number; // For temperature conversions
}

// Test name normalization mapping
const testNameNormalizations: Record<string, string> = {
  // Hemoglobin variations
  'hgb': 'hemoglobin',
  'hb': 'hemoglobin',
  'hg': 'hemoglobin',
  'haemoglobin': 'hemoglobin',
  'hemoglobin a': 'hemoglobin',
  'hgb a': 'hemoglobin',
  
  // Hematocrit variations
  'hct': 'hematocrit',
  'ht': 'hematocrit',
  'pcv': 'hematocrit',
  'packed cell volume': 'hematocrit',
  
  // White blood cell variations
  'wbc': 'white blood cells',
  'white blood cell': 'white blood cells',
  'white cell count': 'white blood cells',
  'leukocytes': 'white blood cells',
  
  // Red blood cell variations
  'rbc': 'red blood cells',
  'red blood cell': 'red blood cells',
  'red cell count': 'red blood cells',
  'erythrocytes': 'red blood cells',
  
  // Other common variations
  'ldl-c': 'ldl cholesterol',
  'hdl-c': 'hdl cholesterol',
  'total cholesterol': 'cholesterol',
  'cholesterol, total': 'cholesterol',
  'bun': 'urea nitrogen',
  'blood urea nitrogen': 'urea nitrogen',
  'gfr': 'glomerular filtration rate',
  'egfr': 'glomerular filtration rate',
  'ast': 'aspartate aminotransferase',
  'sgot': 'aspartate aminotransferase',
  'alt': 'alanine aminotransferase',
  'sgpt': 'alanine aminotransferase',
};

// Normalize test name to handle variations
export function normalizeTestName(testName: string): string {
  const lower = testName.toLowerCase().trim();
  
  // Check for exact match in normalization map
  if (testNameNormalizations[lower]) {
    return testNameNormalizations[lower];
  }
  
  // Check for partial matches
  for (const [variant, normalized] of Object.entries(testNameNormalizations)) {
    if (lower.includes(variant) || variant.includes(lower)) {
      return normalized;
    }
  }
  
  return testName;
}

// Common lab test unit conversions
const conversions: Record<string, UnitConversion[]> = {
  // Cholesterol conversions
  cholesterol: [
    { fromUnit: 'mmol/L', toUnit: 'mg/dL', factor: 38.67 },
    { fromUnit: 'mg/dL', toUnit: 'mmol/L', factor: 0.02586 },
  ],
  
  // Glucose conversions
  glucose: [
    { fromUnit: 'mmol/L', toUnit: 'mg/dL', factor: 18.02 },
    { fromUnit: 'mg/dL', toUnit: 'mmol/L', factor: 0.0555 },
  ],
  
  // Triglycerides conversions
  triglycerides: [
    { fromUnit: 'mmol/L', toUnit: 'mg/dL', factor: 88.57 },
    { fromUnit: 'mg/dL', toUnit: 'mmol/L', factor: 0.01129 },
  ],
  
  // Creatinine conversions
  creatinine: [
    { fromUnit: 'μmol/L', toUnit: 'mg/dL', factor: 0.0113 },
    { fromUnit: 'umol/L', toUnit: 'mg/dL', factor: 0.0113 },
    { fromUnit: 'mg/dL', toUnit: 'μmol/L', factor: 88.42 },
    { fromUnit: 'mg/dL', toUnit: 'umol/L', factor: 88.42 },
  ],
  
  // BUN/Urea conversions
  bun: [
    { fromUnit: 'mmol/L', toUnit: 'mg/dL', factor: 2.8 },
    { fromUnit: 'mg/dL', toUnit: 'mmol/L', factor: 0.357 },
  ],
  urea: [
    { fromUnit: 'mmol/L', toUnit: 'mg/dL', factor: 2.8 },
    { fromUnit: 'mg/dL', toUnit: 'mmol/L', factor: 0.357 },
  ],
  
  // Hemoglobin A1c conversions
  hba1c: [
    { fromUnit: 'mmol/mol', toUnit: '%', factor: 0.09148, offset: 2.152 },
    { fromUnit: '%', toUnit: 'mmol/mol', factor: 10.93, offset: -23.5 },
  ],
  
  // MCHC conversions - correct medical conversion
  mchc: [
    { fromUnit: 'g/L', toUnit: 'g/dL', factor: 0.1 },
    { fromUnit: 'g/dL', toUnit: 'g/L', factor: 10 },
  ],
  
  // Hemoglobin conversions
  hemoglobin: [
    { fromUnit: 'g/L', toUnit: 'g/dL', factor: 0.1 },
    { fromUnit: 'g/dL', toUnit: 'g/L', factor: 10 },
  ],
  
  // Vitamin D conversions
  'vitamin d': [
    { fromUnit: 'nmol/L', toUnit: 'ng/mL', factor: 0.4 },
    { fromUnit: 'ng/mL', toUnit: 'nmol/L', factor: 2.5 },
  ],
  
  // Testosterone conversions (total)
  testosterone: [
    { fromUnit: 'nmol/L', toUnit: 'ng/dL', factor: 28.85 },
    { fromUnit: 'ng/dL', toUnit: 'nmol/L', factor: 0.0347 },
  ],
  
  // Free Testosterone conversions
  'free testosterone': [
    { fromUnit: 'pg/mL', toUnit: 'pmol/L', factor: 3.47 },
    { fromUnit: 'pmol/L', toUnit: 'pg/mL', factor: 0.288 },
    { fromUnit: 'ng/dL', toUnit: 'pmol/L', factor: 34.7 },
    { fromUnit: 'pmol/L', toUnit: 'ng/dL', factor: 0.0288 },
  ],
  
  // Bioavailable Testosterone conversions (same as total)
  'bioavailable testosterone': [
    { fromUnit: 'nmol/L', toUnit: 'ng/dL', factor: 28.85 },
    { fromUnit: 'ng/dL', toUnit: 'nmol/L', factor: 0.0347 },
  ],
  
  // Iron conversions
  iron: [
    { fromUnit: 'μmol/L', toUnit: 'μg/dL', factor: 5.585 },
    { fromUnit: 'umol/L', toUnit: 'ug/dL', factor: 5.585 },
    { fromUnit: 'μg/dL', toUnit: 'μmol/L', factor: 0.179 },
    { fromUnit: 'ug/dL', toUnit: 'umol/L', factor: 0.179 },
  ],
  
  // PSA conversions (1 µg/L = 1 ng/mL)
  psa: [
    { fromUnit: 'µg/L', toUnit: 'ng/mL', factor: 1 },
    { fromUnit: 'μg/L', toUnit: 'ng/mL', factor: 1 },
    { fromUnit: 'ug/L', toUnit: 'ng/mL', factor: 1 },
    { fromUnit: 'ng/mL', toUnit: 'µg/L', factor: 1 },
    { fromUnit: 'ng/mL', toUnit: 'μg/L', factor: 1 },
    { fromUnit: 'ng/mL', toUnit: 'ug/L', factor: 1 },
  ],
  
  // MCV conversions (femtoliters)
  mcv: [
    { fromUnit: 'E', toUnit: 'fL', factor: 1 }, // Common OCR error: "E" for "fL"
    { fromUnit: 'F', toUnit: 'fL', factor: 1 }, // Common OCR error: "F" for "fL"
    { fromUnit: 'fl', toUnit: 'fL', factor: 1 }, // Lowercase variant
    { fromUnit: 'x10^', toUnit: 'fL', factor: 1 }, // Sometimes written as x10^
    { fromUnit: 'f', toUnit: 'fL', factor: 1 }, // Single letter variant
  ],
  
  // MPV conversions (femtoliters)
  mpv: [
    { fromUnit: 'E', toUnit: 'fL', factor: 1 }, // Common OCR error: "E" for "fL"
    { fromUnit: 'F', toUnit: 'fL', factor: 1 }, // Common OCR error: "F" for "fL"
    { fromUnit: 'fl', toUnit: 'fL', factor: 1 }, // Lowercase variant
    { fromUnit: 'x10^', toUnit: 'fL', factor: 1 }, // Sometimes written as x10^
    { fromUnit: 'f', toUnit: 'fL', factor: 1 }, // Single letter variant
  ],
  
  // RBC conversions (M/uL = 10^12/L)
  'red blood cells': [
    { fromUnit: 'M/uL', toUnit: '10^12/L', factor: 1 },
    { fromUnit: '10^12/L', toUnit: 'M/uL', factor: 1 },
    { fromUnit: 'x10^12/L', toUnit: 'M/uL', factor: 1 },
    { fromUnit: '×10^12/L', toUnit: 'M/uL', factor: 1 },
    { fromUnit: 'M/uL', toUnit: 'x10^12/L', factor: 1 },
    { fromUnit: 'M/uL', toUnit: '×10^12/L', factor: 1 },
  ],
  rbc: [
    { fromUnit: 'M/uL', toUnit: '10^12/L', factor: 1 },
    { fromUnit: '10^12/L', toUnit: 'M/uL', factor: 1 },
    { fromUnit: 'x10^12/L', toUnit: 'M/uL', factor: 1 },
    { fromUnit: '×10^12/L', toUnit: 'M/uL', factor: 1 },
  ],
  
  // WBC conversions (K/uL = 10^9/L)
  'white blood cells': [
    { fromUnit: 'K/uL', toUnit: '10^9/L', factor: 1 },
    { fromUnit: '10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: 'x10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: '×10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: 'K/uL', toUnit: 'x10^9/L', factor: 1 },
    { fromUnit: 'K/uL', toUnit: '×10^9/L', factor: 1 },
  ],
  wbc: [
    { fromUnit: 'K/uL', toUnit: '10^9/L', factor: 1 },
    { fromUnit: '10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: 'x10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: '×10^9/L', toUnit: 'K/uL', factor: 1 },
  ],
  
  // Platelet conversions (K/uL = 10^9/L)
  platelets: [
    { fromUnit: 'K/uL', toUnit: '10^9/L', factor: 1 },
    { fromUnit: '10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: 'x10^9/L', toUnit: 'K/uL', factor: 1 },
    { fromUnit: '×10^9/L', toUnit: 'K/uL', factor: 1 },
  ],
};

// Standard units for each test (what we want to convert TO)
const standardUnits: Record<string, string> = {
  cholesterol: 'mg/dL',
  'cholesterol total': 'mg/dL',
  'cholesterol, total': 'mg/dL',
  ldl: 'mg/dL',
  'ldl cholesterol': 'mg/dL',
  hdl: 'mg/dL',
  'hdl cholesterol': 'mg/dL',
  glucose: 'mg/dL',
  triglycerides: 'mg/dL',
  creatinine: 'mg/dL',
  bun: 'mg/dL',
  urea: 'mg/dL',
  'vitamin d': 'ng/mL',
  'vitamin d, 25-hydroxy': 'ng/mL',
  testosterone: 'ng/dL',
  'free testosterone': 'pg/mL',
  'bioavailable testosterone': 'ng/dL',
  iron: 'μg/dL',
  ferritin: 'ng/mL',
  hemoglobin: 'g/dL',
  hematocrit: '%',
  mcv: 'fL',
  mch: 'pg',
  mchc: 'g/dL',  // Standard is g/dL, not g/L
  rdw: '%',
  'platelet count': 'K/uL',
  platelets: 'K/uL',
  wbc: 'K/uL',
  'white blood cells': 'K/uL',
  rbc: 'M/uL',
  'red blood cells': 'M/uL',
  psa: 'ng/mL',
};

/**
 * Normalize unit string to standard format
 */
export function normalizeUnit(unit: string): string {
  if (!unit) return '';
  
  // Convert to lowercase and trim
  let normalized = unit.toLowerCase().trim();
  
  // Common replacements
  const replacements: Record<string, string> = {
    'µ': 'u',
    'micro': 'u',
    '/dl': '/dL',
    '/l': '/L',
    'g/dl': 'g/dL',
    'g/l': 'g/L',
    'mg/dl': 'mg/dL',
    'mmol/l': 'mmol/L',
    'umol/l': 'umol/L',
    'μmol/l': 'umol/L',
    'ng/ml': 'ng/mL',
    'pg/ml': 'pg/mL',
    'ug/l': 'ug/L',
    'μg/l': 'ug/L',
    // E9/L is the same as 10^9/L (scientific notation)
    'e9/l': '10^9/L',
    'e12/l': '10^12/L',
    'e6/l': '10^6/L',
  };
  
  for (const [pattern, replacement] of Object.entries(replacements)) {
    normalized = normalized.replace(pattern, replacement);
  }
  
  return normalized;
}

/**
 * Find the appropriate conversion for a test
 */
function findConversion(testName: string, fromUnit: string, toUnit: string): UnitConversion | null {
  const normalizedName = normalizeTestName(testName);
  const testLower = normalizedName.toLowerCase();
  
  // Try to find conversions for this specific test
  let testConversions: UnitConversion[] = [];
  
  // Check for exact match
  if (conversions[testLower]) {
    testConversions = conversions[testLower];
  } else {
    // Check for partial matches (e.g., "cholesterol total" matches "cholesterol")
    for (const [key, convs] of Object.entries(conversions)) {
      if (testLower.includes(key) || key.includes(testLower)) {
        testConversions = convs;
        break;
      }
    }
  }
  
  // Find matching conversion
  const fromUnitLower = fromUnit.toLowerCase();
  const toUnitLower = toUnit.toLowerCase();
  
  return testConversions.find(conv => 
    conv.fromUnit.toLowerCase() === fromUnitLower && 
    conv.toUnit.toLowerCase() === toUnitLower
  ) || null;
}

/**
 * Get appropriate decimal places for a test type
 */
function getDecimalPlaces(testName: string, value: number): number {
  const testLower = normalizeTestName(testName).toLowerCase();
  
  // Tests that typically use 1 decimal place
  const oneDecimalTests = [
    'cholesterol', 'hdl', 'ldl', 'triglycerides', 'glucose',
    'hemoglobin', 'hematocrit', 'mchc', 'mch', 'mcv', 'rdw',
    'testosterone', 'vitamin d', 'creatinine', 'bun',
    'alt', 'ast', 'alkaline phosphatase', 'bilirubin'
  ];
  
  // Tests that typically use 2 decimal places
  const twoDecimalTests = [
    'tsh', 'free t3', 'free t4', 't3', 't4',
    'cortisol', 'insulin', 'hba1c', 'psa'  // PSA moved here for better precision
  ];
  
  // Tests that typically use no decimal places
  const noDecimalTests = [
    'wbc', 'rbc', 'platelets', 'neutrophils', 'lymphocytes',
    'monocytes', 'eosinophils', 'basophils'
  ];
  
  // Check test categories
  for (const test of noDecimalTests) {
    if (testLower.includes(test)) {
      // Special handling for RBC - always 1 decimal
      if (test === 'rbc' && value < 10) return 1;
      // For very large values (like platelets), use no decimals
      if (value > 100) return 0;
      // For smaller values, use 1 decimal
      return 1;
    }
  }
  
  for (const test of twoDecimalTests) {
    if (testLower.includes(test)) return 2;
  }
  
  for (const test of oneDecimalTests) {
    if (testLower.includes(test)) return 1;
  }
  
  // Default based on value magnitude
  if (value < 0.1) return 3;
  if (value < 10) return 2;
  if (value < 100) return 1;
  return 0;
}

/**
 * Round a value to appropriate decimal places
 */
export function roundValue(value: number, testName: string): number {
  const decimals = getDecimalPlaces(testName, value);
  // Use parseFloat and toFixed to ensure consistent rounding behavior
  return parseFloat(value.toFixed(decimals));
}

/**
 * Convert a value from one unit to another
 */
export function convertValue(
  value: number,
  fromUnit: string,
  toUnit: string,
  testName: string
): { value: number; converted: boolean } {
  // If units are the same, no conversion needed
  if (fromUnit.toLowerCase() === toUnit.toLowerCase()) {
    return { value, converted: false };
  }
  
  const conversion = findConversion(testName, fromUnit, toUnit);
  
  if (conversion) {
    let convertedValue = value * conversion.factor;
    if (conversion.offset) {
      convertedValue += conversion.offset;
    }
    // Round to appropriate decimal places
    convertedValue = roundValue(convertedValue, testName);
    return { value: convertedValue, converted: true };
  }
  
  // No conversion found
  return { value, converted: false };
}

/**
 * Get the standard unit for a test
 */
export function getStandardUnit(testName: string): string | null {
  const normalizedName = normalizeTestName(testName);
  const testLower = normalizedName.toLowerCase();
  
  // Direct match
  if (standardUnits[testLower]) {
    return standardUnits[testLower];
  }
  
  // Partial match
  for (const [key, unit] of Object.entries(standardUnits)) {
    if (testLower.includes(key) || key.includes(testLower)) {
      return unit;
    }
  }
  
  return null;
}

/**
 * Check if two values are drastically different (possible unit mismatch)
 */
export function checkForUnitMismatch(
  value1: number,
  unit1: string,
  value2: number,
  unit2: string,
  testName: string
): { 
  likelyMismatch: boolean; 
  suggestedConversion?: string;
  convertedValue?: number;
} {
  // If units are different, definitely a mismatch
  if (unit1.toLowerCase() !== unit2.toLowerCase()) {
    const standardUnit = getStandardUnit(testName);
    if (standardUnit) {
      // Try to convert both to standard unit
      const conv1 = convertValue(value1, unit1, standardUnit, testName);
      const conv2 = convertValue(value2, unit2, standardUnit, testName);
      
      if (conv1.converted || conv2.converted) {
        return {
          likelyMismatch: true,
          suggestedConversion: `Convert to ${standardUnit}`,
          convertedValue: conv2.converted ? conv2.value : value2
        };
      }
    }
    return { likelyMismatch: true };
  }
  
  // If units are supposedly the same but values differ by more than 10x,
  // there might be a unit labeling error
  const ratio = Math.max(value1, value2) / Math.min(value1, value2);
  if (ratio > 10) {
    // Check common unit confusion patterns
    const testLower = testName.toLowerCase();
    
    // Cholesterol: mg/dL vs mmol/L (factor ~38.67)
    if (testLower.includes('cholesterol') && Math.abs(ratio - 38.67) < 5) {
      return {
        likelyMismatch: true,
        suggestedConversion: 'Possible mg/dL vs mmol/L mismatch'
      };
    }
    
    // Glucose: mg/dL vs mmol/L (factor ~18)
    if (testLower.includes('glucose') && Math.abs(ratio - 18) < 3) {
      return {
        likelyMismatch: true,
        suggestedConversion: 'Possible mg/dL vs mmol/L mismatch'
      };
    }
    
    // MCHC: g/dL vs g/L (factor 10)
    if (testLower.includes('mchc') && Math.abs(ratio - 10) < 1) {
      return {
        likelyMismatch: true,
        suggestedConversion: 'Possible g/dL vs g/L mismatch'
      };
    }
    
    return { likelyMismatch: true };
  }
  
  return { likelyMismatch: false };
}

/**
 * Detect likely unit based on value patterns
 * This helps when units are mislabeled or missing
 */
export function detectLikelyUnit(
  value: number,
  testName: string,
  providedUnit?: string
): {
  likelyUnit: string;
  confidence: 'high' | 'medium' | 'low';
  reason?: string;
  unitMismatch?: boolean;
  correctUnit?: string;
  suggestedConversion?: { toUnit: string; factor: number };
} {
  const normalizedName = normalizeTestName(testName);
  const testLower = normalizedName.toLowerCase();
  
  // MCHC detection - MCHC should ALWAYS be 30-37 g/dL or 320-370 g/L
  if (testLower.includes('mchc')) {
    if (value >= 300 && value <= 370) {
      // This is definitely g/L and needs conversion
      return {
        likelyUnit: 'g/L',
        confidence: 'high',
        reason: 'MCHC value in g/L range (should be 30-37 g/dL)',
        unitMismatch: true,
        correctUnit: 'g/dL',
        suggestedConversion: { toUnit: 'g/dL', factor: 0.1 }
      };
    } else if (value >= 30 && value <= 37) {
      return {
        likelyUnit: 'g/dL',
        confidence: 'high',
        reason: 'MCHC value in correct g/dL range'
      };
    } else if (value >= 30 && value <= 50 && providedUnit && providedUnit.toLowerCase().includes('g/l')) {
      // Value in g/dL range but labeled as g/L - this is a labeling error
      return {
        likelyUnit: 'g/dL',
        confidence: 'high',
        reason: `MCHC value ${value} is in g/dL range but labeled as g/L - likely mislabeled`,
        unitMismatch: true,
        correctUnit: 'g/dL'
      };
    } else if (value > 50 && value < 300) {
      // Ambiguous range, but likely g/L if > 50
      return {
        likelyUnit: 'g/L',
        confidence: 'medium',
        reason: 'MCHC value likely in g/L (normal is 30-37 g/dL)',
        unitMismatch: true,
        correctUnit: 'g/dL',
        suggestedConversion: { toUnit: 'g/dL', factor: 0.1 }
      };
    }
  }
  
  // Cholesterol detection
  if (testLower.includes('cholesterol')) {
    if (value < 20) {
      return {
        likelyUnit: 'mmol/L',
        confidence: 'high',
        reason: 'Value < 20 typical for mmol/L'
      };
    } else if (value > 50) {
      return {
        likelyUnit: 'mg/dL',
        confidence: 'high',
        reason: 'Value > 50 typical for mg/dL'
      };
    }
  }
  
  // Glucose detection
  if (testLower.includes('glucose')) {
    if (value < 30) {
      return {
        likelyUnit: 'mmol/L',
        confidence: 'high',
        reason: 'Value < 30 typical for mmol/L'
      };
    } else if (value > 40) {
      return {
        likelyUnit: 'mg/dL',
        confidence: 'high',
        reason: 'Value > 40 typical for mg/dL'
      };
    }
  }
  
  // Hemoglobin detection - Enhanced with aggressive detection
  if (testLower.includes('hemoglobin') || testLower.includes('hgb') || testLower.includes('hb')) {
    // Very aggressive detection - any value < 20 is almost certainly g/dL
    if (value < 20) {
      // Check if the provided unit claims to be g/L but value suggests g/dL
      if (providedUnit && providedUnit.toLowerCase().includes('g/l')) {
        return {
          likelyUnit: 'g/dL',
          confidence: 'high',
          reason: `Hemoglobin value ${value} is too low for g/L (normal range 120-170 g/L). This appears to be g/dL mislabeled as g/L.`,
          unitMismatch: true,
          correctUnit: 'g/dL'
        };
      }
      return {
        likelyUnit: 'g/dL',
        confidence: 'high',
        reason: 'Hemoglobin value < 20 is certainly g/dL'
      };
    } else if (value >= 20 && value <= 50) {
      // Ambiguous range, but likely g/dL since normal g/L is 120-170
      return {
        likelyUnit: 'g/dL',
        confidence: 'medium',
        reason: 'Hemoglobin value 20-50 is likely g/dL (too low for g/L)'
      };
    } else if (value >= 100 && value <= 200) {
      return {
        likelyUnit: 'g/L',
        confidence: 'high',
        reason: 'Hemoglobin value 100-200 typical for g/L'
      };
    } else if (value > 50 && value < 100) {
      // Very unusual range - might be an error
      return {
        likelyUnit: 'g/L',
        confidence: 'low',
        reason: 'Hemoglobin value unusual - might be g/L or data error'
      };
    }
  }
  
  // Creatinine detection
  if (testLower.includes('creatinine')) {
    if (value < 10) {
      return {
        likelyUnit: 'mg/dL',
        confidence: 'high',
        reason: 'Value < 10 typical for mg/dL'
      };
    } else if (value > 20) {
      return {
        likelyUnit: 'μmol/L',
        confidence: 'high',
        reason: 'Value > 20 typical for μmol/L'
      };
    }
  }
  
  // PSA detection
  if (testLower.includes('psa')) {
    // PSA values are typically < 10 for both ng/mL and μg/L (they're equivalent)
    if (value < 20) {
      return {
        likelyUnit: providedUnit || 'ng/mL',
        confidence: 'medium',
        reason: 'PSA typically < 20'
      };
    }
  }
  
  // Hematocrit detection
  if (testLower.includes('hematocrit') || testLower.includes('hct')) {
    if (value >= 0.30 && value <= 0.60) {
      // Decimal form (0.30-0.60) should be converted to percentage (30-60%)
      return {
        likelyUnit: 'decimal',
        confidence: 'high',
        reason: 'Hematocrit in decimal form (should be 30-60%)',
        unitMismatch: true,
        correctUnit: '%',
        suggestedConversion: { toUnit: '%', factor: 100 }
      };
    } else if (value >= 30 && value <= 60) {
      return {
        likelyUnit: '%',
        confidence: 'high',
        reason: 'Hematocrit in correct percentage range'
      };
    }
  }

  // RBC detection - M/uL and 10^12/L are equivalent
  if (testLower.includes('rbc') || testLower.includes('red blood cell')) {
    if (value < 10) {
      // Check if unit is already in an equivalent form
      if (providedUnit && (providedUnit.includes('10^12') || providedUnit.includes('10¹²') || 
          providedUnit.toLowerCase() === 'm/ul')) {
        return {
          likelyUnit: providedUnit,
          confidence: 'high',
          reason: 'RBC value and unit are correct'
        };
      }
      return {
        likelyUnit: 'M/uL',
        confidence: 'high',
        reason: 'RBC value < 10 typical for M/uL (millions/uL)'
      };
    } else if (value > 1000) {
      return {
        likelyUnit: 'K/uL',
        confidence: 'high',
        reason: 'RBC value > 1000 suggests K/uL (should be M/uL)',
        unitMismatch: true,
        correctUnit: 'M/uL',
        suggestedConversion: { toUnit: 'M/uL', factor: 0.001 }
      };
    }
  }

  // WBC detection - K/uL and 10^9/L are equivalent
  if (testLower.includes('wbc') || testLower.includes('white blood cell')) {
    if (value < 20) {
      // Check if unit is already in an equivalent form
      if (providedUnit && (providedUnit.includes('10^9') || providedUnit.includes('10⁹') || 
          providedUnit.toLowerCase() === 'k/ul')) {
        return {
          likelyUnit: providedUnit,
          confidence: 'high',
          reason: 'WBC value and unit are correct'
        };
      }
      return {
        likelyUnit: 'K/uL',
        confidence: 'high',
        reason: 'WBC value < 20 typical for K/uL (thousands/uL)'
      };
    } else if (value > 1000) {
      return {
        likelyUnit: '/uL',
        confidence: 'high',
        reason: 'WBC value > 1000 suggests absolute count (should be K/uL)',
        unitMismatch: true,
        correctUnit: 'K/uL',
        suggestedConversion: { toUnit: 'K/uL', factor: 0.001 }
      };
    }
  }

  // Platelet detection - K/uL and 10^9/L are equivalent
  if (testLower.includes('platelet')) {
    if (value < 1000) {
      // Check if unit is already in an equivalent form
      if (providedUnit && (providedUnit.includes('10^9') || providedUnit.includes('10⁹') || 
          providedUnit.toLowerCase() === 'k/ul')) {
        return {
          likelyUnit: providedUnit,
          confidence: 'high',
          reason: 'Platelet value and unit are correct'
        };
      }
      return {
        likelyUnit: 'K/uL',
        confidence: 'high',
        reason: 'Platelet value < 1000 typical for K/uL'
      };
    } else if (value > 10000) {
      return {
        likelyUnit: '/uL',
        confidence: 'high',
        reason: 'Platelet value > 10000 suggests absolute count',
        unitMismatch: true,
        correctUnit: 'K/uL',
        suggestedConversion: { toUnit: 'K/uL', factor: 0.001 }
      };
    }
  }

  // Triglycerides detection
  if (testLower.includes('triglyceride')) {
    if (value < 20) {
      return {
        likelyUnit: 'mmol/L',
        confidence: 'high',
        reason: 'Triglycerides < 20 typical for mmol/L'
      };
    } else if (value > 40) {
      return {
        likelyUnit: 'mg/dL',
        confidence: 'high',
        reason: 'Triglycerides > 40 typical for mg/dL'
      };
    }
  }

  // BUN/Urea detection
  if (testLower.includes('bun') || testLower.includes('urea')) {
    if (value < 15) {
      return {
        likelyUnit: 'mmol/L',
        confidence: 'high',
        reason: 'BUN/Urea < 15 typical for mmol/L'
      };
    } else if (value > 20) {
      return {
        likelyUnit: 'mg/dL',
        confidence: 'high',
        reason: 'BUN/Urea > 20 typical for mg/dL'
      };
    }
  }

  // Default: return provided unit with low confidence
  return {
    likelyUnit: providedUnit || 'unknown',
    confidence: 'low',
    reason: 'Unable to determine from value pattern'
  };
}

/**
 * Normalize a set of values to the same unit
 */
export function normalizeValues(
  values: Array<{ value: number; units: string }>,
  testName: string
): {
  normalized: Array<{ value: number; units: string; originalValue?: number; originalUnits?: string }>;
  targetUnit: string;
  hasConversions: boolean;
  hasSmartDetection?: boolean;
  warnings?: string[];
} {
  const normalizedTestName = normalizeTestName(testName);
  const standardUnit = getStandardUnit(normalizedTestName);
  const warnings: string[] = [];
  
  
  // Special handling for MCHC and Hemoglobin - log all values for debugging
  if (normalizedTestName.toLowerCase().includes('mchc') || normalizedTestName.toLowerCase().includes('hemoglobin')) {
    console.group(`🔬 ${normalizedTestName} Unit Analysis`);
    values.forEach((v, i) => {
      console.log(`Value ${i + 1}: ${v.value} ${v.units}`);
      
      if (normalizedTestName.toLowerCase().includes('mchc') && v.value > 50) {
        const warning = `⚠️ MCHC value ${v.value} is too high for g/dL. Normal range is 30-37 g/dL.`;
        console.warn(warning);
        warnings.push(warning);
      }
      
      if (normalizedTestName.toLowerCase().includes('hemoglobin')) {
        if (v.value < 20 && v.units.toLowerCase().includes('g/l')) {
          const warning = `⚠️ Hemoglobin value ${v.value} ${v.units} appears to be mislabeled. Value < 20 suggests g/dL, not g/L.`;
          console.warn(warning);
          warnings.push(warning);
        }
      }
    });
    console.groupEnd();
  }
  
  // First pass: detect likely units based on value patterns
  const valuesWithDetection = values.map(item => {
    const detection = detectLikelyUnit(item.value, normalizedTestName, item.units);
    
    // If high confidence and different from provided unit, use detected unit
    if (detection.confidence === 'high' && 
        detection.likelyUnit !== item.units && 
        detection.likelyUnit !== 'unknown') {
      const warning = `Unit mismatch detected for ${normalizedTestName}: value=${item.value}, provided unit='${item.units}', detected unit='${detection.likelyUnit}' (${detection.reason})`;
      console.warn(warning);
      warnings.push(warning);
      return {
        ...item,
        detectedUnit: detection.likelyUnit,
        originalProvidedUnit: item.units,
        units: detection.likelyUnit // Use detected unit for conversion
      };
    }
    
    return item;
  });
  
  const hasSmartDetection = valuesWithDetection.some(v => 'detectedUnit' in v);
  
  // Count occurrences of each unit (after detection)
  const unitCounts = valuesWithDetection.reduce((acc, v) => {
    const unit = v.units.toLowerCase();
    acc[unit] = (acc[unit] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  // Use the most common unit, or standard unit if available
  let targetUnit = standardUnit || 
    Object.entries(unitCounts).sort((a, b) => b[1] - a[1])[0][0];
  
  let hasConversions = false;
  
  const normalized = valuesWithDetection.map((item, idx) => {
    const converted = convertValue(item.value, item.units, targetUnit, normalizedTestName);
    
    if (converted.converted) {
      hasConversions = true;
      const result = {
        value: converted.value, // Already rounded in convertValue
        units: targetUnit,
        originalValue: item.value,
        originalUnits: 'originalProvidedUnit' in item ? item.originalProvidedUnit : item.units
      };
      
      
      return result;
    }
    
    // Even if not converted, round the value for consistency
    const roundedValue = roundValue(item.value, normalizedTestName);
    
    return { 
      ...item, 
      value: roundedValue,
      units: targetUnit,
      originalUnits: 'originalProvidedUnit' in item ? item.originalProvidedUnit : undefined
    };
  });
  
  
  return { 
    normalized, 
    targetUnit, 
    hasConversions, 
    hasSmartDetection,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}

/**
 * Analyze a dataset for lab-level unit consistency issues
 */
export interface LabUnitIssue {
  reportId: string;
  reportDate: string;
  testName: string;
  value: number;
  providedUnit: string;
  detectedUnit: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
  suggestedConversion?: { toUnit: string; factor: number };
}

export interface LabAnalysisResult {
  labIssues: Map<string, LabUnitIssue[]>; // Key is report ID
  systematicIssues: {
    testName: string;
    affectedReports: string[];
    consistentPattern: string;
    suggestedFix: string;
  }[];
  overallConfidence: 'high' | 'medium' | 'low';
}

/**
 * Analyze multiple lab results for systematic unit issues
 */
export function analyzeLabUnitConsistency(
  labResults: Array<{
    reportId: string;
    reportDate: string;
    testName: string;
    value: number;
    units: string;
  }>
): LabAnalysisResult {
  const labIssues = new Map<string, LabUnitIssue[]>();
  const testPatterns = new Map<string, Map<string, number>>(); // testName -> pattern -> count
  
  // Group results by test name
  const resultsByTest = new Map<string, typeof labResults>();
  labResults.forEach(result => {
    const normalizedTest = normalizeTestName(result.testName);
    if (!resultsByTest.has(normalizedTest)) {
      resultsByTest.set(normalizedTest, []);
    }
    resultsByTest.get(normalizedTest)!.push(result);
  });
  
  // Analyze each test across all reports
  resultsByTest.forEach((results, testName) => {
    results.forEach(result => {
      const detection = detectLikelyUnit(result.value, testName, result.units);
      
      // Track patterns
      const patternKey = `${result.units}->${detection.likelyUnit}`;
      if (!testPatterns.has(testName)) {
        testPatterns.set(testName, new Map());
      }
      const patterns = testPatterns.get(testName)!;
      patterns.set(patternKey, (patterns.get(patternKey) || 0) + 1);
      
      // Record issues with high confidence
      if (detection.confidence === 'high' && detection.unitMismatch) {
        const issue: LabUnitIssue = {
          reportId: result.reportId,
          reportDate: result.reportDate,
          testName: result.testName,
          value: result.value,
          providedUnit: result.units,
          detectedUnit: detection.likelyUnit,
          confidence: detection.confidence,
          reason: detection.reason || 'Unit mismatch detected',
          suggestedConversion: detection.suggestedConversion
        };
        
        if (!labIssues.has(result.reportId)) {
          labIssues.set(result.reportId, []);
        }
        labIssues.get(result.reportId)!.push(issue);
      }
    });
  });
  
  // Identify systematic issues
  const systematicIssues: LabAnalysisResult['systematicIssues'] = [];
  testPatterns.forEach((patterns, testName) => {
    patterns.forEach((count, pattern) => {
      if (count >= 2 && pattern.includes('->') && !pattern.includes('unknown')) {
        const [fromUnit, toUnit] = pattern.split('->');
        if (fromUnit !== toUnit) {
          const affectedResults = labResults.filter(r => 
            normalizeTestName(r.testName) === testName && 
            r.units === fromUnit
          );
          
          systematicIssues.push({
            testName,
            affectedReports: Array.from(new Set(affectedResults.map(r => r.reportId))),
            consistentPattern: `${fromUnit} should be ${toUnit}`,
            suggestedFix: `Convert all ${testName} values from ${fromUnit} to ${toUnit}`
          });
        }
      }
    });
  });
  
  // Calculate overall confidence
  const totalIssues = Array.from(labIssues.values()).flat().length;
  const highConfidenceIssues = Array.from(labIssues.values()).flat()
    .filter(issue => issue.confidence === 'high').length;
  
  let overallConfidence: 'high' | 'medium' | 'low' = 'low';
  if (totalIssues > 0) {
    const confidenceRatio = highConfidenceIssues / totalIssues;
    if (confidenceRatio > 0.8) overallConfidence = 'high';
    else if (confidenceRatio > 0.5) overallConfidence = 'medium';
  }
  
  return {
    labIssues,
    systematicIssues,
    overallConfidence
  };
}

/**
 * Apply batch corrections to lab values based on detected issues
 */
export function applyBatchCorrections(
  values: Array<{ value: number; units: string; reportId: string }>,
  corrections: Map<string, { factor: number; toUnit: string }>
): Array<{ value: number; units: string; reportId: string; corrected?: boolean }> {
  return values.map(item => {
    const correction = corrections.get(item.reportId);
    if (correction) {
      return {
        ...item,
        value: item.value * correction.factor,
        units: correction.toUnit,
        corrected: true
      };
    }
    return item;
  });
}